import { Component, OnInit, inject, DestroyRef, ViewChild, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { TenantService } from 'src/app/services/api/tenant.service';
import { EnvironmentTableComponent } from '../environment-table/environment-table.component';
import { EnvScheduleService } from 'src/app/services/api/env-schedule.service';
import { EnvironmentPaginatorComponent } from '../environment-paginator/environment-paginator.component';
import { Constants } from 'src/app/models/constants';
import { SchedulerComponentUpdateComponent } from './environment-scheduler-component-update/environment-scheduler-component-update.component';
import { InstancesService } from 'src/app/services/api/instances.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { catchError, EMPTY, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { compareVersions } from 'src/app/services/util/version-compare.utils';
import { ComponentsService } from 'src/app/services/api/components.service';

@Component({
    selector: 'app-environment-update-manager',
    templateUrl: './environment-update-manager.component.html',
    styleUrl: './environment-update-manager.component.scss',
    standalone: false
})
export class EnvironmentUpdateManagerComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    @ViewChild(EnvironmentPaginatorComponent) paginatorRef: EnvironmentPaginatorComponent;
    @ViewChild(EnvironmentTableComponent) tableRef: EnvironmentTableComponent;

    tenantId: string;
    componentsLoading = true;
    displayColumns = [
        {
            key: 'selected',
            name: '',
            type: 'radioSelection',
            sortable: false,
            visible: true,
            disable: false
        },
        {
            key: 'name',
            name: 'UPDATE_MANAGER.COMPONENT',
            type: 'text',
            sortable: true,
            visible: true,
            disable: true,
        },
        {
            key: 'currentVersion',
            name: 'UPDATE_MANAGER.CURRENT_VERSION',
            type: 'text',
            visible: true,
            disable: false,
            sortable: true
        },
        {
            key: 'latestVersion',
            name: 'UPDATE_MANAGER.LATEST_VERSION',
            type: 'text',
            visible: true,
            disable: true,
            sortable: true
        },
        {
            key: 'updateManagerSettings',
            name: '',
            type: 'settings',
            visible: true,
            disable: true
        }
    ];

    readonly INSTANCE_DEPLOYING_STATES = [DeployStatus.INITIALIZED, DeployStatus.INITIALIZING, DeployStatus.INITIALIZE_FAILED,
    DeployStatus.DEPLOYING, DeployStatus.TERMINATED, DeployStatus.TERMINATION_FAILED, DeployStatus.TERMINATING,
    DeployStatus.CONFIGURING, DeployStatus.UPDATING, DeployStatus.TESTING,
    DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING,
    DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING,
    DeployStatus.OPERATION_INPROGRESS, DeployStatus.TENANT_AGENT_CREATION_IN_PROGRESS, DeployStatus.STARTING,
    DeployStatus.STOPPING, DeployStatus.STARTING_AGENT, DeployStatus.STOPPING_AGENT, DeployStatus.INPROGRESS];

    showLoader: boolean = true;
    components: any;
    data: any;
    searchTerm: string = '';
    selectedData: any;
    editEnable: boolean;
    pageName = 'environmentUpdateManager';
    searchedResult = [];
    isRefreshing = false;
    instanceId: any;
    componentsResponse = false;
    apiNotFound = false;
    pageIndex = 0;
    paginationSize = 10;
    instance: any;
    disableUpdate: boolean = true;
    allVersions: string[] = [];
    Constants = Constants;

    constructor(private activatedRoute: ActivatedRoute, public componentService: ComponentsService, public sidenavService: SidenavService, private instanceService: InstancesService,
        private toastrService: CustomToastrService, private dialog: MatDialog, public tenantService: TenantService, private cdr: ChangeDetectorRef, private envScheduleService: EnvScheduleService) {

    }

    ngOnInit() {
        this.tenantId = this.activatedRoute.snapshot.params.id;
        this.instanceId = this.activatedRoute.snapshot.params.instanceId;
        this.getInstanceDetails();
        this.loadComponents();
    }
    // Fetch the instance details
    private getInstanceDetails(): void {
        this.instanceService.getInstanceDetails(this.instanceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(data => {
                if (data) {
                    this.instance = data;
                    if (!this.INSTANCE_DEPLOYING_STATES.includes(this.instance?.status)) {
                        this.disableUpdate = false;
                    }
                }
            },
                (error) => {
                    setTimeout(() => {
                        const title = translate('ERRORS.SERVICE_AVAILABILITY');
                        const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
                        this.toastrService.errMessage(message, title);
                    }, 500);
                });
    }
    // Updated table upon pagination rewuest
    onPageChange(event) {
        this.paginationSize = event.pageSize;
        this.pageIndex = event.pageIndex;
        this.tableRef?.pageChanged(event);
    }
    //Updates the index and size of current page
    onTablePageChanged(event) {
        this.pageIndex = event.pageIndex;
        this.paginationSize = event.pageSize;
    }
    //Fetches the latest components
    fetchComponents(): Observable<any> {
        this.showLoader = true;
        this.componentsLoading = true;
        this.editEnable = false;
        this.selectedData = undefined;
        return this.envScheduleService
            .getSchedulerComponents(this.instanceId).pipe(
                switchMap((response: any) => {
                    const isValid =
                        response &&
                        (response.httpStatusCode === 202 || response.httpStatusCode === 200 ) &&
                        !!response.name &&
                        !!response.version;
                    if (!isValid) {
                        return of(null);
                    }
                    const currentVersion = response.version;
                    return this.componentService.getComponents().pipe(
                        map((allComponents) => {
                            const matchingVersions = allComponents
                                .filter(c => c.name === response.name)
                                .map(c => c.version);
                            this.allVersions = matchingVersions;
                            const latestVersion = matchingVersions.reduce(
                                (max, v) =>
                                    compareVersions(v, max) > 0 ? v : max,
                                currentVersion
                            );
                            return {
                                name: allComponents.find(c => c.name === response.name && c.version === currentVersion)?.displayName || response.name,
                                currentVersion,
                                latestVersion
                            };
                        })
                    );
                }),
                tap((result) => {
                    this.components = result ? [result] : [];
                    this.data = this.components;
                    this.componentsResponse = !!result;
                }),
                catchError((error) => {
                    if (error?.httpStatusCode === 404) {
                        this.apiNotFound = true;
                    } else {
                        const title = translate('ERRORS.SERVICE_AVAILABILITY');
                        const message =
                            error?.message ||
                            error?.error?.message ||
                            error?.errmsg ||
                            translate('SERVICE.INTERNAL_SERVER_ERROR');

                        this.toastrService.errMessage(message, title);
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.showLoader = false;
                    this.componentsLoading = false;
                    this.isRefreshing = false;
                }),
                takeUntilDestroyed(this.destroyRef)
            );
    }

    //Resets the pageindex to 0
    resetPaginator() {
        this.paginatorRef.pageIndex = 0;
    }
    //resets the table view with latest changes
    refreshClick(): void {
        this.isRefreshing = true;
        if (this.paginatorRef) {
            this.paginatorRef.pageIndex = 0;
        }
        this.loadComponents();
    }

    //searches the record based on input
    search(searchTerm?: string): void {
        this.searchTerm = searchTerm;
    }
    //Applies search results to table
    searchedItems(result): void {
        this.searchedResult = result;
        this.cdr.detectChanges();
    }
    //Fetched the row data selected
    getApproveData(data): void {
        this.editEnable = true;
        this.selectedData = data;
    }

    loadComponents(): void {
        this.fetchComponents().subscribe();
    }

    //Opens the right side nav bar to update the component version
    onUpdateClick(): void {
    const { name, currentVersion } = this.selectedData;
        const otherVersions = this.allVersions?.filter(v => compareVersions(v, currentVersion) > 0)
            .sort((a, b) => compareVersions(a, b)) || [];
        const data = {
            name,
            currentVersion,
            versions: otherVersions,
            instanceId: this.instanceId
        };
        this.sidenavService
            .openInRightSidenav(SchedulerComponentUpdateComponent, data)
            .subscribe(result => {
                if (result?.updated) {
                    this.showLoader = true;
                    this.componentsLoading = true;
                    setTimeout(() => {
                        this.loadComponents();
                    }, 3000);
                }
            });
}
    // Error block
    openErrorDialog(data): void {
        this.dialog.open(ErrorDialogComponent, {
            minHeight: '342px',
            width: '630px',
            panelClass: 'flex-dialog',
            data: {
                title: translate('SCHEDULE_DETAILS.SCHEDULE_VALIDATION_ERROR'),
                descriptionError: data
            }
        });
    }
}


